import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@dreamtree-org/twreact-ui';
import { BadgeCheck, ArrowRight, Lock } from 'lucide-react';
import { EmptyState, SectionCard } from '@/components';
import { cartTotals } from '../lib/cart';
import { lineKey } from '../lib/variants';
import { useQuery } from '@/lib/useQuery';
import { useStorefront } from '../StorefrontContext';
import CartLineItem from '../components/CartLineItem';
import CheckoutSteps from '../components/CheckoutSteps';
import PriceSummary from '../components/PriceSummary';
import PinCheck from '../components/PinCheck';
import TrustBadges from '../components/TrustBadges';

/** WEB-05 cart (mockup 04, step 1). One seller per order; a unit is unique (qty 1), items sold by quantity take 1..10. */
export default function Cart() {
  const sf = useStorefront();
  const navigate = useNavigate();
  const [removing, setRemoving] = useState(null);
  const items = sf.cart.items;
  const totals = cartTotals(items);
  const seller = useQuery('sfSeller', { slug: sf.seller.slug, name: sf.seller.name }, { select: (r) => r.data });

  const [qtyError, setQtyError] = useState(null);
  // Lines are a listing and, for one with options, its variant (MERCH-87).
  const changeQty = async (i, qty) => {
    setRemoving(lineKey(i.listing_id, i.variant_id));
    try {
      const r = await sf.setCartQty(i.listing_id, qty, i.variant_id);
      setQtyError(r.ok ? null : r.error);
    } finally {
      setRemoving(null);
    }
  };

  const remove = async (i) => {
    setRemoving(lineKey(i.listing_id, i.variant_id));
    try {
      await sf.removeFromCart(i.listing_id, i.variant_id);
    } finally {
      setRemoving(null);
    }
  };

  if (!items.length) {
    return (
      <div className="space-y-4">
        <CheckoutSteps current={0} />
        <EmptyState title="Your cart is empty" action={<Button onClick={() => navigate('/shop')}>Browse the shop</Button>}>
          Add an item to hold it while you check out.
        </EmptyState>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <CheckoutSteps current={0} />
      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_380px]">
        <div className="space-y-4">
          <SectionCard
            title={`Your cart (${totals.count} item${totals.count === 1 ? '' : 's'})`}
            actions={<Link to="/shop" className="inline-flex min-h-11 items-center text-sm font-medium text-primary-700 hover:underline">Continue shopping</Link>}
          >
            <div className="flex items-center gap-2 rounded-md bg-primary-50 px-3 py-2 text-sm text-primary-800">
              <BadgeCheck className="h-4 w-4 shrink-0" aria-hidden="true" />
              Sold and shipped by {(sf.cart.seller && sf.cart.seller.name) || sf.seller.name} · one seller per order
            </div>
            <div className="divide-y divide-[var(--app-border)]">
              {items.map((i) => (
                <CartLineItem key={lineKey(i.listing_id, i.variant_id)} item={i} onRemove={() => remove(i)} onQty={(q) => changeQty(i, q)} removing={removing === lineKey(i.listing_id, i.variant_id)} />
              ))}
            </div>
            {qtyError ? <p className="text-sm text-error-600" role="alert">{qtyError}</p> : null}
          </SectionCard>
          <div className="hidden lg:block">
            <SectionCard title={`Why shop with ${sf.brand.name}?`}><TrustBadges seller={seller.data} /></SectionCard>
          </div>
        </div>

        <div className="space-y-4">
          <SectionCard title="Delivery">
            <PinCheck compact />
          </SectionCard>
          <SectionCard title="Price details">
            <PriceSummary t={totals} />
            <Button size="lg" fullWidth rightIcon={<ArrowRight className="h-4 w-4" />} onClick={() => navigate('/checkout')}>
              Check out
            </Button>
            <p className="flex items-center gap-1.5 text-xs opacity-70"><Lock className="h-3.5 w-3.5" aria-hidden="true" />Coupons, delivery speed and payment are chosen at checkout.</p>
          </SectionCard>
        </div>
      </div>
    </div>
  );
}
